/**
 * QuoteCartPage Custom Element
 * Manages the quote cart page with cart items and quote form
 */
class QuoteCartPage extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = 'sms_quote_cart';
    this.CONTACT_STORAGE_KEY = 'sms_quote_contact_info';
  }

  connectedCallback() {
    this.initializeElements();
    this.renderCartItems();
    this.prePopulateContactInfo();
    this.setupEventListeners();
  }

  prePopulateContactInfo() {
    // Pre-populate contact info from localStorage if available
    try {
      const storedContact = localStorage.getItem(this.CONTACT_STORAGE_KEY);
      if (storedContact) {
        const contactInfo = JSON.parse(storedContact);

        const firstNameInput = this.querySelector('#quote-first-name');
        const lastNameInput = this.querySelector('#quote-last-name');
        const emailInput = this.querySelector('#quote-email');

        if (firstNameInput && contactInfo.firstName) {
          firstNameInput.value = contactInfo.firstName;
        }
        if (lastNameInput && contactInfo.lastName) {
          lastNameInput.value = contactInfo.lastName;
        }
        if (emailInput && contactInfo.email) {
          emailInput.value = contactInfo.email;
        }
      }
    } catch (error) {
      console.error('Error retrieving stored contact info:', error);
    }
  }

  handleFieldChange() {
    // Update storage whenever a field is changed
    const firstName = this.querySelector('#quote-first-name')?.value || '';
    const lastName = this.querySelector('#quote-last-name')?.value || '';
    const email = this.querySelector('#quote-email')?.value || '';

    if (firstName || lastName || email) {
      this.saveContactInfo(firstName, lastName, email);
    }
  }

  saveContactInfo(firstName, lastName, email) {
    try {
      const contactInfo = { firstName, lastName, email };
      localStorage.setItem(this.CONTACT_STORAGE_KEY, JSON.stringify(contactInfo));
    } catch (error) {
      console.error('Error saving contact info:', error);
    }
  }

  initializeElements() {
    this.emptyState = this.querySelector('#quote-cart-empty');
    this.cartForm = this.querySelector('#quote-cart-form');
    this.summaryWrapper = this.querySelector('#quote-cart-summary-wrapper');
    this.headingTop = this.querySelector('.heading-top');
    this.summaryDescription = this.querySelector('.quote-cart-summary-description');
    this.tbody = this.querySelector('#quote-cart-items-body');
    this.summarySubtotal = this.querySelector('#quote-cart-summary-subtotal');
    this.summaryTotal = this.querySelector('#quote-cart-summary-total');
    this.quoteForm = this.querySelector('#quote-cart-quote-form');
  }

  // Utility functions
  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return '$0.00';
    }
  }

  stripPriceFromValue(value) {
    if (typeof value === 'string') {
      // Remove all price patterns like (+$270.03) or (-$36.00) while keeping all option text
      return value.replace(/ \(\+\$[\d,.]+\)/g, '').replace(/ \(-\$[\d,.]+\)/g, '');
    }
    return value;
  }

  getQuoteCart() {
    try {
      const cartData = localStorage.getItem(this.STORAGE_KEY);
      return cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      return { items: [], createdAt: new Date().toISOString() };
    }
  }

  saveQuoteCart(cart) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  }

  // CRITICAL: Update only totals via textContent - NEVER touch the form
  updateTotals() {
    const cart = this.getQuoteCart();
    const totalPrice = cart.items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);

    if (this.summarySubtotal) this.summarySubtotal.textContent = this.formatMoney(totalPrice);
    if (this.summaryTotal) this.summaryTotal.textContent = this.formatMoney(totalPrice);
  }

  // Build a single cart item row
  buildCartItemRow(item) {
    const itemTotal = (item.price || 0) * (item.quantity || 1);
    const row = document.createElement('tr');
    row.className = 'cart-item main-cart-wrapper';
    row.id = `QuoteCartItem-${item.id}`;

    // Build properties HTML
    let propertiesHTML = '';
    if (item.properties && Object.keys(item.properties).length > 0) {
      // Check for bundle components first
      if (item.properties._bundle_components) {
        const components = item.properties._bundle_components.split('||');
        propertiesHTML = '<dl class="bundle-components">';
        components.forEach(comp => {
          propertiesHTML += `<div class="product-option"><dd>${comp}</dd></div>`;
        });
        propertiesHTML += '</dl>';
      } else {
        propertiesHTML = '<dl>';
        for (const [key, value] of Object.entries(item.properties)) {
          if (key.charAt(0) !== '_' && value) {
            const strippedValue = this.stripPriceFromValue(value);
            // Split by comma and display each item on a separate line
            const items = strippedValue.split(',').map(item => item.trim()).filter(item => item);
            propertiesHTML += `<div class="product-option"><dt>${key}</dt>`;
            items.forEach(item => {
              propertiesHTML += `<dd>${item}</dd>`;
            });
            propertiesHTML += `</div>`;
          }
        }
        propertiesHTML += '</dl>';
      }
    }

    const vendor = item.properties?._vendor || '';
    let imageUrl = item.image || '';
    if (imageUrl && imageUrl.startsWith('files/')) {
      imageUrl = imageUrl.replace('files/', 'https://savemyserver.com/cdn/shop/files/');
    }

    const imageHTML = imageUrl ? `<a href="#" class="cart-item__link" aria-hidden="true" tabindex="-1"><img src="${imageUrl}" alt="${item.title || 'Product'}" class="cart-item__image" loading="lazy" width="150" height="150"></a>` : '';

    row.innerHTML = `
      <td class="cart-item__media">${imageHTML}<div class="loading-overlay hidden"><div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div></td>
      <td class="cart-item__details">
        ${vendor ? `<span class="cart-item__vendor">${vendor}</span>` : ''}
        <a href="#" class="cart-item__name h4 break">${item.title || 'Configured Product'}</a>
        <div class="item-price-wrapper price-inner"><div class="product-option price-original">${this.formatMoney(item.price || 0)}</div></div>
        ${propertiesHTML}
        <div class="cart-item__note-section"><div class="cart-item__note-wrapper"><label for="note-${item.id}">Order special instructions</label><textarea id="note-${item.id}" class="cart-item__note-input" data-item-id="${item.id}" placeholder="Add a note for this item...">${item.properties?.note || ''}</textarea></div></div>
      </td>
      <td class="cart-item__quantity">
        <div class="cart-item__quantity-wrapper">
          <label class="visually-hidden" for="QuoteQuantity-${item.id}">Quantity</label>
          <div class="quantity cart-quantity">
            <button class="quantity__button no-js-hidden quote-qty-minus" type="button" data-item-id="${item.id}"><span class="visually-hidden">Decrease</span><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></svg></button>
            <input class="quantity__input" type="number" name="quantity" value="${item.quantity || 1}" min="1" id="QuoteQuantity-${item.id}" data-item-id="${item.id}">
            <button class="quantity__button no-js-hidden quote-qty-plus" type="button" data-item-id="${item.id}"><span class="visually-hidden">Increase</span><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></svg></button>
          </div>
          <cart-remove-button id="QuoteRemove-${item.id}"><a href="#" class="cart-remove-button" data-item-id="${item.id}"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon icon-remove"><path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path><path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"></path></svg></a></cart-remove-button>
        </div>
      </td>
      <td class="cart-item__totals right"><div class="cart-item__price-wrapper"><span class="price price--end">${this.formatMoney(itemTotal)}</span></div></td>
    `;

    return row;
  }

  // Render cart items only - NEVER touches the quote form area
  renderCartItems() {
    const cart = this.getQuoteCart();

    if (cart.items.length === 0) {
      this.emptyState.style.display = 'block';
      this.cartForm.style.display = 'none';
      if (this.summaryWrapper) this.summaryWrapper.style.display = 'none';
      if (this.summaryDescription) this.summaryDescription.style.display = 'none';
      return;
    }

    this.emptyState.style.display = 'none';
    this.cartForm.style.display = 'block';
    if (this.summaryWrapper) this.summaryWrapper.style.display = 'block';
    if (this.summaryDescription) this.summaryDescription.style.display = 'block';

    // Clear and rebuild cart items tbody only
    this.tbody.innerHTML = '';
    cart.items.forEach(item => this.tbody.appendChild(this.buildCartItemRow(item)));

    // Update totals via textContent only
    this.updateTotals();
  }

  // Cart item operations - surgical updates only
  handleQuantityChange(itemId, change) {
    const cart = this.getQuoteCart();
    const item = cart.items.find(i => i.id === itemId);
    if (!item) return;

    item.quantity = Math.max(1, (item.quantity || 1) + change);
    this.saveQuoteCart(cart);

    // Update only the specific elements - no re-render
    const qtyInput = document.getElementById(`QuoteQuantity-${itemId}`);
    if (qtyInput) qtyInput.value = item.quantity;

    const totalEl = this.querySelector(`#QuoteCartItem-${itemId} .price--end`);
    if (totalEl) totalEl.textContent = this.formatMoney((item.price || 0) * item.quantity);

    this.updateTotals();
    document.dispatchEvent(new CustomEvent('quote-cart-icon-update', { detail: { cart } }));
  }

  handleRemoveItem(itemId) {
    const cart = this.getQuoteCart();
    cart.items = cart.items.filter(item => item.id !== itemId);
    this.saveQuoteCart(cart);
    this.renderCartItems();
    document.dispatchEvent(new CustomEvent('quote-cart-updated', { detail: { cart, action: 'remove' } }));
  }

  handleNoteUpdate(itemId, note) {
    const cart = this.getQuoteCart();
    const item = cart.items.find(i => i.id === itemId);
    if (!item) return;

    if (!item.properties) item.properties = {};
    if (note) {
      item.properties.note = note;
    } else {
      delete item.properties.note;
    }
    this.saveQuoteCart(cart);
    // No UI update needed - note is already in textarea
  }

  // Form submission - the ONLY code that touches the form
  async handleFormSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const cart = this.getQuoteCart();

    if (cart.items.length === 0) {
      if (window.QuoteSuccessDialog) {
        window.QuoteSuccessDialog.show({
          type: 'error',
          title: 'Empty Cart',
          message: 'Your quote cart is empty! Please add items before submitting.',
          buttonText: 'Continue Shopping',
          buttonUrl: '/collections/all'
        });
      } else {
        alert('Your quote cart is empty!');
      }
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    try {
      const lineItems = cart.items.map(item => ({
        variantId: item.variantId,
        quantity: item.quantity || 1,
        customAttributes: Object.entries(item.properties || {}).map(([key, value]) => ({ key, value: value.toString() })),
      }));

      const itemNotes = cart.items
        .filter(item => item.properties?.note)
        .map(item => `${item.title}: ${item.properties.note}`)
        .join('\n\n');

      const response = await fetch(`${window.SMS_GADGET_CONFIG.baseUrl}/createDraftOrder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          input: {
            email: formData.get('email'),
            phoneNumber: formData.get('phone'),
            note: itemNotes || undefined,
            lineItems,
            customAttributes: [
              {
                key: '_source',
                value: 'Quote Cart Page'
              }
            ],
            shippingAddress: {
              firstName: formData.get('firstName'),
              lastName: formData.get('lastName'),
              address1: formData.get('address'),
              address2: formData.get('address2') || undefined,
              city: formData.get('city'),
              provinceCode: formData.get('province'),
              countryCode: formData.get('country'),
              zip: formData.get('zip'),
              phone: formData.get('phone')
            }
          },
          store: window.SMS_GADGET_CONFIG.storeName
        })
      });

      if (!response.ok) throw new Error('Failed to create draft order');
      await response.json();

      const totalQty = cart.items.reduce((sum, item) => sum + (item.quantity || 1), 0);

      // Clear cart first
      const emptyCart = { items: [], createdAt: new Date().toISOString() };
      this.saveQuoteCart(emptyCart);
      localStorage.removeItem('sms_quote_cart_email');
      document.dispatchEvent(new CustomEvent('quote-cart-updated', { detail: { cart: emptyCart, action: 'clear' } }));

      // Show success dialog and redirect on close
      if (window.QuoteSuccessDialog) {
        window.QuoteSuccessDialog.show({
          title: 'Quote Request Submitted!',
          message: `We've created a draft order with ${totalQty} item${totalQty > 1 ? 's' : ''}. Our team will review and contact you soon.`,
          buttonText: 'Continue Shopping',
          buttonUrl: '/'
        });
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error submitting quote:', error);

      // Show error dialog
      if (window.QuoteSuccessDialog) {
        window.QuoteSuccessDialog.show({
          type: 'error',
          title: 'Submission Error',
          message: 'There was an error submitting this form. Please check your information and try again.',
          buttonText: 'Try Again',
          onClose: () => {
            // Reset button state when dialog closes
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }
        });
      } else {
        alert('Failed to submit quote request. Please try again or contact support.');
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  // Event listeners setup
  setupEventListeners() {
    // Event delegation - ONE listener on tbody for all cart interactions
    if (this.tbody) {
      this.tbody.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.cart-remove-button');
        if (removeBtn) {
          e.preventDefault();
          const itemId = removeBtn.dataset.itemId;
          if (itemId && confirm('Remove this item from the quote cart?')) {
            this.handleRemoveItem(itemId);
          }
          return;
        }

        const minusBtn = e.target.closest('.quote-qty-minus');
        if (minusBtn) {
          e.preventDefault();
          this.handleQuantityChange(minusBtn.dataset.itemId, -1);
          return;
        }

        const plusBtn = e.target.closest('.quote-qty-plus');
        if (plusBtn) {
          e.preventDefault();
          this.handleQuantityChange(plusBtn.dataset.itemId, 1);
          return;
        }
      });

      this.tbody.addEventListener('change', (e) => {
        const qtyInput = e.target.closest('.quantity__input');
        if (qtyInput) {
          const newQty = Math.max(1, parseInt(qtyInput.value) || 1);
          qtyInput.value = newQty;
          const currentQty = this.getQuoteCart().items.find(i => i.id === qtyInput.dataset.itemId)?.quantity || 1;
          this.handleQuantityChange(qtyInput.dataset.itemId, newQty - currentQty);
        }
      });

      this.tbody.addEventListener('blur', (e) => {
        const noteInput = e.target.closest('.cart-item__note-input');
        if (noteInput) {
          this.handleNoteUpdate(noteInput.dataset.itemId, noteInput.value.trim());
        }
      }, true);
    }

    // Form submission handler - the ONLY interaction with the form
    if (this.quoteForm) {
      this.quoteForm.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    // Contact info field change listeners to update localStorage
    const firstNameInput = this.querySelector('#quote-first-name');
    const lastNameInput = this.querySelector('#quote-last-name');
    const emailInput = this.querySelector('#quote-email');

    if (firstNameInput) {
      firstNameInput.addEventListener('blur', this.handleFieldChange.bind(this));
    }
    if (lastNameInput) {
      lastNameInput.addEventListener('blur', this.handleFieldChange.bind(this));
    }
    if (emailInput) {
      emailInput.addEventListener('blur', this.handleFieldChange.bind(this));
    }

    // External cart updates (from other pages adding items)
    document.addEventListener('quote-cart-updated', () => this.renderCartItems());
  }
}

// Define the custom element
if (!customElements.get('quote-cart-page')) {
  customElements.define('quote-cart-page', QuoteCartPage);
}
